import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";
import CatalogueGrid from "./CatalogueGrid";
import { ShareButtons } from "../components/ShareButtons";
import { ELink } from "../components/Editable";
import { useLang } from "../i18n/LanguageContext";

// Page d'atterrissage SEO par occasion : /occasions/tournoi-hockey, etc.
// Le contenu (titres, descriptions FR/EN, mots-clés) vient de la collection « occasions ».
export default function OccasionPage() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const [occ, setOcc] = useState(null);  // null = chargement, false = introuvable

  useEffect(() => {
    setOcc(null);
    api.get(`/occasions/${slug}`).then((r) => setOcc(r.data)).catch(() => setOcc(false));
  }, [slug]);

  if (occ === false) {
    return (
      <div className="pt-40 pb-28 text-center px-6" data-testid="occasion-not-found">
        <h1 className="font-serif text-4xl mb-6">{t("category.not_found")}</h1>
        <ELink editPath="link.occasion_back" to="/occasions" className="text-gold hover:text-champagne transition-colors duration-300 inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> {t("occasions.back")}
        </ELink>
      </div>
    );
  }
  if (!occ) {
    return <div className="flex items-center justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>;
  }

  const title = lang === "en" ? occ.title_en : occ.title_fr;
  const desc = lang === "en" ? occ.desc_en : occ.desc_fr;
  const meta = lang === "en" ? occ.meta_en : occ.meta_fr;
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: lang === "en" ? "Home" : "Accueil", item: "https://mrtrophee.ca/" },
      { "@type": "ListItem", position: 2, name: lang === "en" ? "By occasion" : "Par occasion", item: "https://mrtrophee.ca/occasions" },
      { "@type": "ListItem", position: 3, name: title },
    ],
  };

  return (
    <div className="pt-32 pb-28 max-w-7xl mx-auto px-6 lg:px-10" data-testid="occasion-page">
      <SEO title={`${title} | Mr Trophée`} description={meta || desc} path={`/occasions/${slug}`} jsonLd={breadcrumbLd}
        keywords={(lang === "en" ? occ.seo_keywords_en : occ.seo_keywords) || occ.seo_keywords} />
      <FadeUp>
        <ELink editPath="link.occasion_back" to="/occasions" data-testid="occasion-back"
          className="inline-flex items-center gap-2 text-xs text-ivory/60 hover:text-gold uppercase tracking-widest mb-6 transition-colors duration-300">
          <ArrowLeft className="w-3.5 h-3.5" /> {t("occasions.back")}
        </ELink>
        <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("occasions.kicker")}</p>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-4" data-testid="occasion-title">{title}</h1>
        <p className="text-ivory/60 text-sm leading-relaxed max-w-3xl mb-4" data-testid="occasion-intro">{desc}</p>
        <ShareButtons title={title} label={t("footer.share_page")} />
        <div className="mb-6" />
      </FadeUp>
      <CatalogueGrid productsOverride={occ.products || []} />
    </div>
  );
}
